import { EyeIcon, EyeOffIcon, TriangleAlertIcon } from 'lucide-react';
import * as React from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/**
 * Password field with a show/hide toggle and a Caps Lock hint.
 * Works with react-hook-form's `register` (ref is a plain prop in React 19).
 */
function PasswordInput({
  className,
  capsLockHint = true,
  onKeyDown,
  onKeyUp,
  onBlur,
  ...props
}: Omit<React.ComponentProps<'input'>, 'type'> & { capsLockHint?: boolean }) {
  const [visible, setVisible] = React.useState(false);
  const [capsLock, setCapsLock] = React.useState(false);
  const check = (e: React.KeyboardEvent<HTMLInputElement>) => setCapsLock(e.getModifierState('CapsLock'));

  return (
    <div className="grid gap-1.5">
      <div className="relative">
        <Input
          type={visible ? 'text' : 'password'}
          className={cn('pr-10', className)}
          onKeyDown={(e) => {
            check(e);
            onKeyDown?.(e);
          }}
          onKeyUp={(e) => {
            check(e);
            onKeyUp?.(e);
          }}
          onBlur={(e) => {
            setCapsLock(false);
            onBlur?.(e);
          }}
          {...props}
        />
        <button
          type="button"
          aria-label={visible ? 'Hide password' : 'Show password'}
          aria-pressed={visible}
          onClick={() => setVisible((v) => !v)}
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 absolute inset-y-0 right-0 flex w-10 items-center justify-center outline-none transition-colors focus-visible:ring-[3px]"
        >
          {visible ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
        </button>
      </div>
      {capsLockHint && capsLock && (
        <p className="text-muted-foreground flex items-center gap-1.5 text-xs" role="status">
          <TriangleAlertIcon className="text-warning size-3.5" aria-hidden />
          Caps Lock is on
        </p>
      )}
    </div>
  );
}

export { PasswordInput };
